import {useEffect, useRef, useState} from 'react';
import {useRetrieveInboundBridgeMetadata} from './use-analytics-api';
import {BridgeType, NodeFilterData} from '../inbound-bridging';
import {NodeMetadataList} from '@amzn/f3-excelsior-analytics-api/clients/analyticsapiforexcelsioranalytics';

export const MAX_ALLOWED_DATE_RANGE = 14;

export function useInboundBridgeFilterData(
    bridgeType: BridgeType,
    tenant: {business: string; country: string},
    networkLevel: boolean,
    clientConfiguration: any,
    createErrorListener: (header: string) => (e: any) => void,
    fetchDataCall?: (params: CommonBridgeDataRequest) => void
) {
    const [nodeFilterDataMap, setNodeFilterDataMap] = useState<
        Record<
            string,
            {
                balanceDateEarliest: string;
                balanceDateLatest: string;
                horizonPairs: {
                    dateUnit: string;
                    fromHorizon: number;
                    toHorizon: number;
                }[];
                granularityValues: {
                    [key: string]: string[];
                };
            }
        >
    >({});
    const [selectedFilter, setSelectedFilter] = useState<NodeFilterData | undefined>();

    const nodeFilterDataInitialized = useRef(false);
    const isLoadingFilterData = useRef<boolean>(true);

    const {
        execute: executeRetrieveInboundBridgeMetadata,
        status: inboundBridgeMetadataStatus,
        value: getInboundBridgeMetadataResponse,
    } = useRetrieveInboundBridgeMetadata(clientConfiguration, createErrorListener('RetrieveInboundBridgeMetadata failed'), [
        tenant,
    ]);

    isLoadingFilterData.current = ['idle', 'pending'].includes(inboundBridgeMetadataStatus);

    if (!nodeFilterDataInitialized.current) {
        executeRetrieveInboundBridgeMetadata({
            forecastType: 'sop',
            ...tenant,
            bridgeType,
        });
        nodeFilterDataInitialized.current = true;
    }

    useEffect(() => {
        if (
            !isLoadingFilterData.current &&
            getInboundBridgeMetadataResponse?.nodes &&
            getInboundBridgeMetadataResponse.nodes.length > 0
        ) {
            if (networkLevel) {
                const response = reduceNodeMetadataToNetwork(getInboundBridgeMetadataResponse.nodes);
                setNodeFilterDataMap({
                    ALL: response,
                });

                const {balanceDateEarliest, balanceDateLatest, horizonPairs, granularityValues} = response;

                const latestDate = new Date(balanceDateLatest);
                const earliestDate = new Date(balanceDateEarliest);
                const startDate = new Date(latestDate.getTime() - MAX_ALLOWED_DATE_RANGE * 24 * 60 * 60 * 1000);
                const cappedStartDate = startDate < earliestDate ? earliestDate : startDate;

                setSelectedFilter({
                    node: 'ALL',
                    balanceDateEarliest,
                    balanceDateLatest,
                    balanceDateStart: cappedStartDate.toISOString().split('T')[0],
                    balanceDateEnd: latestDate.toISOString().split('T')[0],
                    fromHorizonInWeeks: horizonPairs[0].fromHorizon,
                    toHorizonInWeeks: horizonPairs[0].toHorizon,
                    channels: granularityValues['channels'] ?? [],
                    sourceLocations: [],
                    ibTypes: granularityValues['ibTypes'] ?? [],
                    tempZones: granularityValues['tempZones'] ?? [],
                });

                fetchDataCall?.({
                    nodes: ['ALL'],
                    balanceDateStart: cappedStartDate.toISOString().split('T')[0],
                    balanceDateEnd: latestDate.toISOString().split('T')[0],
                    fromHorizonInWeeks: horizonPairs[0].fromHorizon,
                    toHorizonInWeeks: horizonPairs[0].toHorizon,
                });
            } else {
                const response = Object.fromEntries(
                    getInboundBridgeMetadataResponse.nodes.map((n) => [
                        n.node,
                        {
                            balanceDateEarliest: n.balanceDateEarliest,
                            balanceDateLatest: n.balanceDateLatest,
                            horizonPairs: n.horizonPairs
                                .filter((hp) => hp.dateUnit === 'WEEK')
                                .sort((a, b) => a.fromHorizon - b.fromHorizon)
                                .map((hp) => ({
                                    dateUnit: hp.dateUnit,
                                    fromHorizon: hp.fromHorizon,
                                    toHorizon: hp.toHorizon,
                                })),
                            granularityValues: n.granularityValues,
                        },
                    ])
                );

                setNodeFilterDataMap(response);

                const defaultNode = getInboundBridgeMetadataResponse.nodes[0].node;
                const {balanceDateEarliest, balanceDateLatest, horizonPairs, granularityValues} = response[defaultNode];

                const latestDate = new Date(balanceDateLatest);
                const earliestDate = new Date(balanceDateEarliest);
                const startDate = new Date(latestDate.getTime() - MAX_ALLOWED_DATE_RANGE * 24 * 60 * 60 * 1000);
                const cappedStartDate = startDate < earliestDate ? earliestDate : startDate;

                setSelectedFilter({
                    node: defaultNode,
                    balanceDateEarliest,
                    balanceDateLatest,
                    balanceDateStart: cappedStartDate.toISOString().split('T')[0],
                    balanceDateEnd: latestDate.toISOString().split('T')[0],
                    fromHorizonInWeeks: horizonPairs[0].fromHorizon,
                    toHorizonInWeeks: horizonPairs[0].toHorizon,
                    channels: granularityValues['channels'] ?? [],
                    sourceLocations: granularityValues['sourceLocations'] ?? [],
                    ibTypes: granularityValues['ibTypes'] ?? [],
                    tempZones: granularityValues['tempZones'] ?? [],
                });

                fetchDataCall?.({
                    nodes: [defaultNode],
                    balanceDateStart: cappedStartDate.toISOString().split('T')[0],
                    balanceDateEnd: latestDate.toISOString().split('T')[0],
                    fromHorizonInWeeks: horizonPairs[0].fromHorizon,
                    toHorizonInWeeks: horizonPairs[0].toHorizon,
                });
            }
        }
    }, [isLoadingFilterData.current]);

    return {
        nodeFilterDataMap,
        selectedFilter,
        setSelectedFilter,
        isLoadingFilterData: isLoadingFilterData.current,
    };
}

function reduceNodeMetadataToNetwork(nodes: NodeMetadataList): {
    balanceDateEarliest: string;
    balanceDateLatest: string;
    horizonPairs: {
        dateUnit: string;
        fromHorizon: number;
        toHorizon: number;
    }[];
    granularityValues: {
        [key: string]: string[];
    };
} {
    const balanceDateEarliest = nodes.reduce(
        (min, node) => (node.balanceDateEarliest < min ? node.balanceDateEarliest : min),
        nodes[0].balanceDateEarliest
    );
    const balanceDateLatest = nodes.reduce(
        (max, node) => (node.balanceDateLatest > max ? node.balanceDateLatest : max),
        nodes[0].balanceDateLatest
    );
    const horizonPairs = nodes.reduce(
        (acc, node) => {
            node.horizonPairs
                .filter((hp) => hp.dateUnit === 'WEEK')
                .forEach((hp) => {
                    if (!acc.find((x) => x.fromHorizon === hp.fromHorizon && x.toHorizon === hp.toHorizon)) {
                        acc.push(hp);
                    }
                });
            return acc;
        },
        [] as {
            dateUnit: string;
            fromHorizon: number;
            toHorizon: number;
        }[]
    );
    const granularityValues = nodes.reduce(
        (acc, node) => {
            Object.keys(node.granularityValues).forEach((key) => {
                if (!acc[key]) {
                    acc[key] = [];
                }
                node.granularityValues[key].forEach((value) => {
                    if (!acc[key].includes(value)) {
                        acc[key].push(value);
                    }
                });
            });
            return acc;
        },
        {} as {
            [key: string]: string[];
        }
    );

    return {
        balanceDateEarliest,
        balanceDateLatest,
        horizonPairs,
        granularityValues,
    };
}

export type CommonBridgeDataRequest = {
    nodes: string[];
    balanceDateStart: string;
    balanceDateEnd: string;
    fromHorizonInWeeks: number;
    toHorizonInWeeks: number;
};
